import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import {
  ClientEmploymentHistory,
  ClientVMSSubInfo,
  TalentVmsEmploymentHistory,
  TalentVmsPreviousAnswers,
  TalentVmsRelationshipDisclosure
} from '../../../../typings';
import { ActivityType, VmsEmploymentType, RelationshipType } from "../../../enums";
import { VmsSubmittalApiService } from "../vms-submittal-api.service";
import { ActivatedRoute, Router } from "@angular/router";
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {UtilService} from '../../talent-profile/shared/util.service';

@Component({
  selector: 'app-vms-submittal-employment-history-and-relationship',
  templateUrl: './vms-submittal-employment-history-and-relationship.component.html',
  styleUrl: './vms-submittal-employment-history-and-relationship.component.scss',
  standalone: false,
})
export class VmsSubmittalEmploymentHistoryAndRelationshipComponent {
  protected readonly VmsEmploymentType = VmsEmploymentType;

  vmsEmploymentHistoryAndRelationshipForm: FormGroup;
  selectedEmploymentTypeIds: number[] = [];
  employmentHistoryDetailFields: { control: string; placeholder: string; required: boolean }[];
  allEmploymentHistoryFields: { control: string; placeholder: string; required: boolean }[];
  employmentHistoryCompleted: boolean = false;
  relationshipDisclosureCompleted: boolean = false;
  ernstAndYoungEmploymentHistoryCompleted: boolean = false;

  @Input() clientEmploymentHistory: ClientEmploymentHistory;
  @Input() clientName: string;
  @Input() clientVmsSubInfo: ClientVMSSubInfo;
  @Input() personId: number;
  employmentTypes = [
    { id: VmsEmploymentType.Permanent, label: 'talent-happy-path.talent-vms.employment-history.employment-type.permanent' },
    { id: VmsEmploymentType.Contract, label: 'talent-happy-path.talent-vms.employment-history.employment-type.contract' },
    { id: VmsEmploymentType.Vendor, label: 'talent-happy-path.talent-vms.employment-history.employment-type.vendor' },
    { id: VmsEmploymentType.Intern, label: 'talent-happy-path.talent-vms.employment-history.employment-type.intern' }
  ];
  relationshipTypes = [
    { id: RelationshipType.Aunt_Uncle, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.aunt-uncle' },
    { id: RelationshipType.Child, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.child' },
    { id: RelationshipType.Grandparent, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.grandparent' },
    { id: RelationshipType.In_Law, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.in-law' },
    { id: RelationshipType.Other, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.other' },
    { id: RelationshipType.Parent, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.parent' },
    { id: RelationshipType.Sibling, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.sibling' },
    { id: RelationshipType.Spouse_Partner, label: 'talent-happy-path.talent-vms.relationship-disclosure.relationship.options.spouse-partner' }
  ];
  isSaving: boolean = false;
  @Output() pageLoading = new EventEmitter<boolean>();
  gatherUUID: string;

  constructor(
    private fb: FormBuilder,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private vmsSubApiService: VmsSubmittalApiService,
    private router: Router,
    private route: ActivatedRoute,
    private utils: UtilService
  ) { }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.gatherUUID = this.route.snapshot.queryParams['gatheruuid'];
    if(this.isFromGather()) {
      if(!this.clientVmsSubInfo.vmsSubGatherInfo.employmentHistoryRequired
        && !this.clientVmsSubInfo.vmsSubGatherInfo.relationshipDisclosureRequired
        && !this.clientVmsSubInfo.vmsSubGatherInfo.ernstAndYoungEmploymentHistoryRequired) {
        this.nextPage();
      }
    }
    // Check if both employment history and relationship disclosure are already completed
    const observables = [];
    if (this.showEmploymentHistory()) {
      observables.push(
        this.vmsSubApiService.readVmsEmploymentHistoryCompleted(
          this.talentHappyPathDataService.orderId,
          this.talentHappyPathDataService.talent.personId
        )
      );
    }
    if (this.showRelationshipDisclosure()) {
      observables.push(
        this.vmsSubApiService.readVmsRelationshipDisclosureCompleted(
          this.talentHappyPathDataService.orderId,
          this.talentHappyPathDataService.talent.personId
        )
      );
    }
    if (this.showErnstAndYoungHistory()) {
      observables.push(
        this.vmsSubApiService.readErnstAndYoungHistoryCompleted(
          this.talentHappyPathDataService.orderId,
          this.talentHappyPathDataService.talent.personId
        )
      )
    }
    if (observables.length > 0) {
      forkJoin(observables).pipe(
        switchMap((results) => {
          let resultIndex = 0;
          this.employmentHistoryCompleted = this.showEmploymentHistory()
            ? Boolean(results[resultIndex++])
            : true;
          this.relationshipDisclosureCompleted = this.showRelationshipDisclosure()
            ? Boolean(results[resultIndex++])
            : true;
          this.ernstAndYoungEmploymentHistoryCompleted = this.showErnstAndYoungHistory()
            ? Boolean(results[resultIndex++])
            : true;
          if (this.employmentHistoryCompleted && this.relationshipDisclosureCompleted && this.ernstAndYoungEmploymentHistoryCompleted) {
            this.router.navigateByUrl('thank-you', {skipLocationChange: true});
            return of(null);
          }
          const needsPrepopulation =
            (!this.employmentHistoryCompleted && this.showEmploymentHistory())
            || (!this.relationshipDisclosureCompleted && this.showRelationshipDisclosure())
            || (!this.ernstAndYoungEmploymentHistoryCompleted && this.showErnstAndYoungHistory());
          if (!needsPrepopulation) {
            return of(null);
          }
          return this.vmsSubApiService.readPreviousVmsAnswers(
            this.talentHappyPathDataService.orderId,
            this.talentHappyPathDataService.talent.personId,
            this.talentHappyPathDataService.gatherToken
          ).pipe(
            catchError(() => of(null))
          );
        })
      ).subscribe((previousAnswers) => {
        if (previousAnswers) {
          this.applyPreviousAnswers(previousAnswers);
        }
        this.pageLoading.emit(false);
      }, error => {
        this.pageLoading.emit(true);
      });
    } else {
      this.pageLoading.emit(false);
    }
    this.employmentHistoryDetailFields = [
      { control: 'ManagerName', placeholder: 'Manager Name', required: this.clientEmploymentHistory.managerNameRequired },
      { control: 'Title', placeholder: 'Title', required: this.clientEmploymentHistory.titleRequired },
      { control: 'Department', placeholder: 'Department', required: this.clientEmploymentHistory.departmentRequired },
      { control: 'PreviousClientEmail', placeholder: 'Email', required: this.clientEmploymentHistory.knownEmailRequired },
      { control: 'JobLocation', placeholder: 'City (Location)', required: this.clientEmploymentHistory.jobLocationRequired }
    ];
    this.allEmploymentHistoryFields = this.employmentHistoryDetailFields.concat([
      { control: 'StartDate', placeholder: 'Start Date', required: this.clientEmploymentHistory.employmentDateRequired },
      { control: 'EndDate', placeholder: 'End Date', required: this.clientEmploymentHistory.employmentDateRequired },
      { control: 'EndReason', placeholder: 'Reason for ending', required: this.clientEmploymentHistory.endReasonRequired },
      { control: 'PreviousAgency', placeholder: 'Agency Name', required: this.clientEmploymentHistory.previousAgencyRequired }
    ]);

    this.vmsEmploymentHistoryAndRelationshipForm = this.fb.group({
      workedWithClient: [null],
      selectedEmploymentTypeIds: [[], this.clientEmploymentHistory.employmentTypeRequired ? Validators.required : []],

      permanentStartDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required : []],
      permanentEndDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required : []],
      permanentManagerName: [null, this.clientEmploymentHistory.managerNameRequired ? Validators.required : []],
      permanentTitle: [null, this.clientEmploymentHistory.titleRequired ? Validators.required : []],
      permanentDepartment: [null, this.clientEmploymentHistory.departmentRequired ? Validators.required : []],
      permanentEndReason: [null, this.clientEmploymentHistory.endReasonRequired ? Validators.required : []],
      permanentJobLocation: [null, this.clientEmploymentHistory.jobLocationRequired ? Validators.required : []],
      permanentPreviousAgency: [null, this.clientEmploymentHistory.previousAgencyRequired ? Validators.required : []],
      permanentPreviousClientEmail: [null, this.clientEmploymentHistory.knownEmailRequired ? [Validators.required, Validators.email] : []],

      contractStartDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      contractEndDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      contractManagerName: [null, this.clientEmploymentHistory.managerNameRequired ? Validators.required : []],
      contractTitle: [null, this.clientEmploymentHistory.titleRequired ? Validators.required : []],
      contractDepartment: [null, this.clientEmploymentHistory.departmentRequired ? Validators.required : []],
      contractEndReason: [null, this.clientEmploymentHistory.endReasonRequired ? Validators.required : []],
      contractJobLocation: [null, this.clientEmploymentHistory.jobLocationRequired ? Validators.required : []],
      contractPreviousAgency: [null, this.clientEmploymentHistory.previousAgencyRequired ? Validators.required : []],
      contractPreviousClientEmail: [null, this.clientEmploymentHistory.knownEmailRequired ? [Validators.required, Validators.email] : []],

      vendorStartDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      vendorEndDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      vendorManagerName: [null, this.clientEmploymentHistory.managerNameRequired ? Validators.required : []],
      vendorDepartment: [null, this.clientEmploymentHistory.departmentRequired ? Validators.required : []],
      vendorEndReason: [null, this.clientEmploymentHistory.endReasonRequired ? Validators.required : []],
      vendorJobLocation: [null, this.clientEmploymentHistory.jobLocationRequired ? Validators.required : []],
      vendorPreviousAgency: [null, this.clientEmploymentHistory.previousAgencyRequired ? Validators.required : []],
      vendorTitle: [null, this.clientEmploymentHistory.titleRequired ? Validators.required : []],
      vendorPreviousClientEmail: [null, this.clientEmploymentHistory.knownEmailRequired ? [Validators.required, Validators.email] : []],

      internStartDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      internEndDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required: []],
      internManagerName: [null, this.clientEmploymentHistory.managerNameRequired ? Validators.required : []],
      internTitle: [null, this.clientEmploymentHistory.titleRequired ? Validators.required : []],
      internDepartment: [null, this.clientEmploymentHistory.departmentRequired ? Validators.required : []],
      internEndReason: [null, this.clientEmploymentHistory.endReasonRequired ? Validators.required : []],
      internJobLocation: [null, this.clientEmploymentHistory.jobLocationRequired ? Validators.required : []],
      internPreviousAgency: [null, this.clientEmploymentHistory.previousAgencyRequired ? Validators.required : []],
      internPreviousClientEmail: [null, this.clientEmploymentHistory.knownEmailRequired ? [Validators.required, Validators.email] : []],

      // for when the employment type is not required
      mostRecentStartDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required : []],
      mostRecentEndDate: [null, this.clientEmploymentHistory.employmentDateRequired ? Validators.required : []],
      mostRecentManagerName: [null, this.clientEmploymentHistory.managerNameRequired ? Validators.required : []],
      mostRecentTitle: [null, this.clientEmploymentHistory.titleRequired ? Validators.required : []],
      mostRecentDepartment: [null, this.clientEmploymentHistory.departmentRequired ? Validators.required : []],
      mostRecentEndReason: [null, this.clientEmploymentHistory.endReasonRequired ? Validators.required : []],
      mostRecentJobLocation: [null, this.clientEmploymentHistory.jobLocationRequired ? Validators.required : []],
      mostRecentPreviousAgency: [null, this.clientEmploymentHistory.previousAgencyRequired ? Validators.required : []],
      mostRecentPreviousClientEmail: [null, this.clientEmploymentHistory.knownEmailRequired ? [Validators.required, Validators.email] : []],

      familyMemberWorksWithClient: [null],
      familyMemberName: [null, this.clientVmsSubInfo.relationshipDisclosureRequired ? Validators.required : []],
      relationshipTypeId: [null, this.clientVmsSubInfo.relationshipDisclosureRequired ? Validators.required : []],

      ernstAndYoungEmploymentHistory: [null, this.showErnstAndYoungHistory() ? Validators.required : []],
      ernstAndYoungEngagedOnClientAccount: [null]
    });

    let prevSelected: number[] = this.vmsEmploymentHistoryAndRelationshipForm.get('selectedEmploymentTypeIds').value ?? [];
    this.vmsEmploymentHistoryAndRelationshipForm.get('selectedEmploymentTypeIds').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newSelected: number[]) => {
        const deselected = prevSelected.filter(id => !newSelected.includes(id));
        deselected.forEach(id => this.clearEmploymentTypeFields(id));
        prevSelected = [...newSelected];
      });

    this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberWorksWithClient').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value === false) {
          this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
            familyMemberName: null,
            relationshipTypeId: null
          });
          this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberName').markAsPristine();
          this.vmsEmploymentHistoryAndRelationshipForm.get('relationshipTypeId').markAsPristine();
        }
      });

    this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEmploymentHistory').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const engagedControl = this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEngagedOnClientAccount');

        if (value === true && this.showErnstAndYoungHistory()) {
          engagedControl.setValidators(Validators.required);
        } else {
          engagedControl.clearValidators();
          engagedControl.setValue(null);
          engagedControl.markAsPristine();
        }

        engagedControl.updateValueAndValidity();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyPreviousAnswers(previous: TalentVmsPreviousAnswers): void {
    if (!this.employmentHistoryCompleted && this.showEmploymentHistory()
        && previous.talentVmsEmploymentHistory && previous.talentVmsEmploymentHistory.length > 0) {
      const rows = previous.talentVmsEmploymentHistory;
      if (this.clientEmploymentHistory.employmentTypeRequired) {
        const selectedIds: number[] = [];
        rows.forEach(row => {
          if (row.vmsEmploymentType == null) return;
          const type = this.employmentTypes.find(t => t.id === row.vmsEmploymentType);
          if (!type) return;
          const prefix = this.getPrefixFromLabel(type.label);
          if (!prefix) return;
          if (row.hasEmploymentHistory) {
            selectedIds.push(row.vmsEmploymentType);
            this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
              [`${prefix}StartDate`]: row.startDate,
              [`${prefix}EndDate`]: row.endDate,
              [`${prefix}ManagerName`]: row.managerName,
              [`${prefix}Title`]: row.title,
              [`${prefix}Department`]: row.department,
              [`${prefix}EndReason`]: row.endReason,
              [`${prefix}JobLocation`]: row.jobLocation,
              [`${prefix}PreviousAgency`]: row.previousAgency,
              [`${prefix}PreviousClientEmail`]: row.previousClientEmail,
            });
          }
        });
        if (selectedIds.length > 0) {
          this.vmsEmploymentHistoryAndRelationshipForm.patchValue({ selectedEmploymentTypeIds: selectedIds });
        }
      } else {
        const row = rows[0];
        this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
          workedWithClient: row.hasEmploymentHistory ?? false
        });
        if (row.hasEmploymentHistory) {
          this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
            mostRecentStartDate: row.startDate,
            mostRecentEndDate: row.endDate,
            mostRecentManagerName: row.managerName,
            mostRecentTitle: row.title,
            mostRecentDepartment: row.department,
            mostRecentEndReason: row.endReason,
            mostRecentJobLocation: row.jobLocation,
            mostRecentPreviousAgency: row.previousAgency,
            mostRecentPreviousClientEmail: row.previousClientEmail,
          });
        }
      }
    }

    if (!this.relationshipDisclosureCompleted && this.showRelationshipDisclosure()
        && previous.talentVmsRelationshipDisclosure) {
      const rd = previous.talentVmsRelationshipDisclosure;
      this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
        familyMemberWorksWithClient: rd.hasRelationship ?? false,
        familyMemberName: rd.familyMemberName,
        relationshipTypeId: rd.relationshipType,
      });
    }

    if (!this.ernstAndYoungEmploymentHistoryCompleted && this.showErnstAndYoungHistory()
        && previous.talentVmsErnstAndYoungEmploymentHistory) {
      const ey = previous.talentVmsErnstAndYoungEmploymentHistory;
      this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
        ernstAndYoungEmploymentHistory: ey.hasEmploymentHistory ?? null,
      });
      if (ey.hasEmploymentHistory) {
        this.vmsEmploymentHistoryAndRelationshipForm.patchValue({
          ernstAndYoungEngagedOnClientAccount: ey.engagedOnClientAccount ?? null,
        });
      }
    }
  }

  get isRelationshipDisclosureValid(): boolean {
    const familyMemberWorksWithClient = this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberWorksWithClient').value;
    if (!this.relationshipDisclosureCompleted && this.showRelationshipDisclosure()) {
      if (familyMemberWorksWithClient === null) return false;
      if (familyMemberWorksWithClient === false) return true;
    } else {
        return true;
    }
    return (
      this.relationshipDisclosureCompleted || (familyMemberWorksWithClient === true &&
      this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberName').valid &&
      this.vmsEmploymentHistoryAndRelationshipForm.get('relationshipTypeId').valid)
    );
  }

  get isErnstAndYoungHistoryValid(): boolean {
    const ernstAndYoungEngagedOnClientAccount = this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEngagedOnClientAccount');
    const ernstAndYoungEmploymentHistory = this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEmploymentHistory');
    if (!this.ernstAndYoungEmploymentHistoryCompleted && this.showErnstAndYoungHistory()) {
      if (ernstAndYoungEmploymentHistory.value == null) return false;
      if (ernstAndYoungEmploymentHistory.value === false) return true;
    } else {
      return true;
    }
    return (
      this.ernstAndYoungEmploymentHistoryCompleted || (ernstAndYoungEmploymentHistory.value === true
      && ernstAndYoungEmploymentHistory.valid
      && ernstAndYoungEngagedOnClientAccount.valid)
    );
  }

  private areRequiredFieldsValid(prefix: string): boolean {
    return this.allEmploymentHistoryFields
      .filter(field => field.required)
      .every(field => {
        if (field.control === 'PreviousAgency' && (prefix === 'permanent' || prefix === 'intern')) {
          return true;
        }
        const control = this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}${field.control}`);
        return !control || control.valid;
      });
  }

  get isEmploymentHistoryValid(): boolean {
    if (this.employmentHistoryCompleted || !this.showEmploymentHistory()
      || (!this.clientEmploymentHistory.employmentTypeRequired
        && this.vmsEmploymentHistoryAndRelationshipForm.get('workedWithClient').value === false)) {
      return true;
    }
    if (this.clientEmploymentHistory.employmentTypeRequired) {
      const selectedIds: number[] = this.vmsEmploymentHistoryAndRelationshipForm.get('selectedEmploymentTypeIds').value ?? [];
      for (const type of this.employmentTypes) {
        if (selectedIds.includes(type.id)) {
          const prefix = this.getPrefixFromLabel(type.label);
          if (prefix && !this.areRequiredFieldsValid(prefix)) {
            return false;
          }
        }
      }
    } else {
      if (!this.areRequiredFieldsValid('mostRecent')) {
        return false;
      }
    }
    return true;
  }

  get isOnlyEmploymentTypeRequired(): boolean {
    const empHistory = this.clientVmsSubInfo?.clientEmploymentHistory;
    if (!empHistory) return false;
    return empHistory.employmentTypeRequired === true &&
      Object.entries(empHistory)
        .filter(([key]) => key !== 'employmentTypeRequired')
        .every(([, val]) => typeof val === 'boolean' && val === false);
  }

  get isAnyEmploymentHistoryRequired(): boolean {
    const empHistory = this.clientVmsSubInfo?.clientEmploymentHistory;
    if (!empHistory) return false;
    return Object.values(empHistory)
      .filter((val): val is boolean => typeof val === 'boolean')
      .some(val => val === true);
  }

  disableSubmit() {
    if (this.isSaving) {
      return true;
    }
    return !this.isEmploymentHistoryValid
      || !this.isRelationshipDisclosureValid
      || !this.isErnstAndYoungHistoryValid
  }

  submitEmploymentHistoryAndRelationship() {
    this.isSaving = true;
    const selectedIds: number[] = this.vmsEmploymentHistoryAndRelationshipForm.get('selectedEmploymentTypeIds').value ?? [];
    let talentVmsFullEmploymentHistory: TalentVmsEmploymentHistory[] = [];

    if (!this.employmentHistoryCompleted && this.showEmploymentHistory()) {
      if (!this.clientEmploymentHistory.employmentTypeRequired) {
        talentVmsFullEmploymentHistory.push({
          personId: this.talentHappyPathDataService.talent.personId,
          orderId: this.talentHappyPathDataService.orderId,
          startDate: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentStartDate').value,
          endDate: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentEndDate').value,
          title: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentTitle').value,
          managerName: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentManagerName').value,
          department: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentDepartment').value,
          endReason: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentEndReason').value,
          jobLocation: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentJobLocation').value,
          previousAgency: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentPreviousAgency').value,
          previousClientEmail: this.vmsEmploymentHistoryAndRelationshipForm.get('mostRecentPreviousClientEmail').value,
          vmsEmploymentType: null,
          hasEmploymentHistory: this.vmsEmploymentHistoryAndRelationshipForm.get('workedWithClient').value === true
        });
      } else {
        this.employmentTypes.forEach(type => {
          const prefix = this.getPrefixFromLabel(type.label);
          const isSelected = selectedIds.includes(type.id);

          talentVmsFullEmploymentHistory.push({
            personId: this.talentHappyPathDataService.talent.personId,
            orderId: this.talentHappyPathDataService.orderId,
            startDate: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}StartDate`).value : null,
            endDate: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}EndDate`).value : null,
            title: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}Title`).value : null,
            managerName: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}ManagerName`).value : null,
            department: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}Department`).value : null,
            endReason: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}EndReason`).value : null,
            jobLocation: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}JobLocation`).value : null,
            previousAgency: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}PreviousAgency`).value : null,
            previousClientEmail: isSelected ? this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}PreviousClientEmail`).value : null,
            vmsEmploymentType: type.id,
            hasEmploymentHistory: isSelected
          });
        });
      }
    } else {
      talentVmsFullEmploymentHistory = null;
    }
    let talentVmsRelationshipDisclosure : TalentVmsRelationshipDisclosure = !this.relationshipDisclosureCompleted && this.showRelationshipDisclosure() ?
      {
        personId: this.talentHappyPathDataService.talent.personId,
        orderId: this.talentHappyPathDataService.orderId,
        familyMemberName: this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberName').value,
        relationshipType: this.vmsEmploymentHistoryAndRelationshipForm.get('relationshipTypeId').value,
        hasRelationship: this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberWorksWithClient').value !== null
          ? this.vmsEmploymentHistoryAndRelationshipForm.get('familyMemberWorksWithClient').value : false
      } : null;
    let talentErnstAndYoungEmploymentHistory = !this.ernstAndYoungEmploymentHistoryCompleted && this.showErnstAndYoungHistory()
      ? {
        personId: this.talentHappyPathDataService.talent.personId,
        orderId: this.talentHappyPathDataService.orderId,
        hasEmploymentHistory: this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEmploymentHistory').value,
        engagedOnClientAccount: this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEngagedOnClientAccount').value != null
          ? this.vmsEmploymentHistoryAndRelationshipForm.get('ernstAndYoungEngagedOnClientAccount').value : false
      } : null;
    this.vmsSubApiService.saveTalentVmsEmploymentHistoryAndRelationship(talentVmsFullEmploymentHistory, talentVmsRelationshipDisclosure, talentErnstAndYoungEmploymentHistory).subscribe(() => {
      this.isSaving = false;
      this.nextPage();
    }, error => {
      console.error("Error saving Employment History and Relationship Disclosure", error);
    })
  }

  /**
   * Handler for date range changes in employment history sections.
   * @param event The date range change event object containing startDate, endDate, employmentTypeLabel.
   */
  onEmploymentDateRangeChange(event: { startDate: Date, endDate: Date, employmentTypeLabel?: string }): void {
    const prefix = event.employmentTypeLabel ? this.getPrefixFromLabel(event.employmentTypeLabel) : "mostRecent";
    this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}StartDate`)?.setValue(event.startDate ? this.utils.dateToString(event.startDate) : null);
    this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}EndDate`)?.setValue(event.endDate ? this.utils.dateToString(event.endDate) : null);
  }

  showEmploymentHistory(): boolean {
    return (this.isFromGather() && this.clientVmsSubInfo.vmsSubGatherInfo.employmentHistoryRequired)
      || (!this.isFromGather() && this.isAnyEmploymentHistoryRequired);
  }

  showRelationshipDisclosure(): boolean {
    return (this.isFromGather() && this.clientVmsSubInfo.vmsSubGatherInfo.relationshipDisclosureRequired)
      || (!this.isFromGather() && this.clientVmsSubInfo.relationshipDisclosureRequired);
  }

  showErnstAndYoungHistory(): boolean {
    return (this.isFromGather() && this.clientVmsSubInfo.vmsSubGatherInfo.ernstAndYoungEmploymentHistoryRequired)
      || (!this.isFromGather() && this.clientVmsSubInfo.ernstAndYoungEmploymentHistoryRequired);
  }

  clearEmploymentTypeFields(employmentTypeId: number) {
    const type = this.employmentTypes.find(t => t.id === employmentTypeId);
    if (type) {
      const prefix = this.getPrefixFromLabel(type.label);
      if (prefix) {
        this.allEmploymentHistoryFields.forEach(field => {
          this.vmsEmploymentHistoryAndRelationshipForm.get(`${prefix}${field.control}`)?.setValue(null);
        });
      }
    }
  }

  private getPrefixFromLabel(label: string): string {
    return label.split('.').pop()?.toLowerCase() ?? '';
  }

  deleteTab(employmentTypeId: number) {
    // Remove the employment type from the selected list
    const control = this.vmsEmploymentHistoryAndRelationshipForm.get('selectedEmploymentTypeIds');
    const current = control?.value ?? [];
    control?.setValue(current.filter((id: number) => id !== employmentTypeId));

    // Clear related fields for the removed employment type
    this.clearEmploymentTypeFields(employmentTypeId);
  }

  nextPage(): void {
    if(this.isFromGather()) {
      // gatheruuid is the gather tracking token, not the numeric gather id: the IIQ pages hand it
      // straight to /api/gather/token/{token} and into the do-this-later email link.
void this.router.navigate(['/initial-interview'], { queryParams: { gatheruuid: this.gatherUUID }, skipLocationChange: true });
    }
    else {
      this.router.navigate(['thank-you'], {queryParams: { from: 'vms-submittal' }, skipLocationChange: true});
    }
  }

  doThisLater() {
    this.vmsSubApiService.createActivity(
      ActivityType.VMS_SUBMITTAL_DO_THIS_LATER_CLICKED, this.talentHappyPathDataService.talent.personId, this.talentHappyPathDataService.orderId, this.talentHappyPathDataService.postingId
    ).subscribe({
      next: () => {
        console.log('Vms submittal do this later activity created successfully.');
      },
      error: (err) => {
        console.error('Failed to create vms submittal do this later activity:', err);
      }
    });

    this.router.navigateByUrl('/initial-interview?gatheruuid='+ this.gatherUUID, { skipLocationChange: true });
  }

  isFromGather(): boolean {
    return this.talentHappyPathDataService.gather != null && this.talentHappyPathDataService.gather.gatherId != null;
  }
}
